import { useEffect, useState } from "react";
import { creatorFetch, creatorToken } from "../adminApi";
import { LOGO_URL } from "@/data";
import LoginScreen from "./LoginScreen";
import Home from "./Home";
import { ShopTab, ProductsTab, StoryTab } from "./ArtisanMode";
import { StageTab, PerformancesTab, BookingSettingsTab, RequestsTab } from "./ArtistMode";
import ProfileTab from "./ProfileTab";

/* ক্রিয়েটর-পোর্টাল — নিজের দোকান/মঞ্চের ঘর (গ্রামবাসী এখানে প্রায় আসেই না) */
export default function CreatorPanel({ onExit, onSaved, goCustomer }) {
  const [authed, setAuthed] = useState(!!creatorToken());
  const [tab, setTab] = useState("home"); // home | shop | products | story | ai | stage | performances | bsettings | requests | orders | messages | profile
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [pendingAdd, setPendingAdd] = useState(false);

  const load = async () => {
    try {
      const mine = await creatorFetch("/creator/mine");
      setData(mine);
      try {
        const m = await creatorFetch("/creator/me");
        setMe(m.creator || null);
      } catch {}
      if (onSaved) onSaved();
    } catch {
      localStorage.removeItem("rom_creator_token");
      setAuthed(false);
    }
  };

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  // ঘরের ভেতরের ছোট পথ-নির্দেশ (দোকান/মঞ্চের বোতাম থেকে)
  useEffect(() => {
    const onNav = (e) => {
      setTab(e.detail?.tab || "home");
      if (e.detail?.add) setPendingAdd(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
    window.addEventListener("creator-nav", onNav);
    return () => window.removeEventListener("creator-nav", onNav);
  }, []);

  useEffect(() => {
    if (tab !== "products" && tab !== "performances") setPendingAdd(false);
  }, [tab]);

  if (!authed) return <LoginScreen onOk={() => setAuthed(true)} />;

  const isArtist = me?.creator_type === "artist";
  const refresh = () => {
    load();
    if (onSaved) onSaved();
  };

  const logout = async () => {
    try {
      await creatorFetch("/creator/logout", { method: "POST" });
    } catch {}
    localStorage.removeItem("rom_creator_token");
    setAuthed(false);
    setTab("home");
  };

  const section = () => {
    if (!data) return <p className="text-sm text-sand/60">লোড হচ্ছে…</p>;
    switch (tab) {
      case "shop":
        return <ShopTab data={data} refresh={refresh} goCustomer={goCustomer} />;
      case "products":
        return <ProductsTab key={pendingAdd ? "add" : "list"} data={data} refresh={refresh} startAdd={pendingAdd} />;
      case "story":
        return <StoryTab data={data} refresh={refresh} />;
      case "stage":
        return <StageTab data={data} refresh={refresh} goCustomer={goCustomer} />;
      case "performances":
        return <PerformancesTab data={data} refresh={refresh} startAdd={pendingAdd} />;
      case "bsettings":
        return <BookingSettingsTab data={data} refresh={refresh} />;
      case "requests":
        return <RequestsTab data={data} refresh={refresh} />;
      case "orders":
        return <OrdersTab data={data} />;
      case "messages":
        return <MessagesTab data={data} />;
      case "profile":
        return <div className="max-w-2xl space-y-10">{me && <ProfileTab creator={me} refresh={refresh} />}</div>;
      default:
        return <Home creator={me || { name: "…", creator_type: "artisan" }} data={data} onNav={setTab} onExit={onExit} onLogout={logout} />;
    }
  };

  const homeNav = (t) => {
    setTab(t);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const crumbs =
    tab === "home" ? [] :
    tab === "shop" || tab === "products" || tab === "story" ? [["products", "আমার পণ্য"], ["story", "নিজের গল্প"]] :
    tab === "stage" || tab === "performances" || tab === "bsettings" || tab === "requests" ? [["performances", "আমার পরিবেশনা"], ["bsettings", "বুকিং-সেটিংস"], ["requests", "বুকিং-অনুরোধ"]] :
    [["profile", "প্রোফাইল ও QR"]];

  return (
    <div className="min-h-screen" data-testid="creator-panel">
      <div className="mx-auto max-w-3xl px-5 py-6 sm:px-8">
        {/* মাথা — সরল */}
        <div className="flex items-center justify-between">
          <button data-testid="creator-home-btn" onClick={() => setTab("home")} className="flex items-center gap-3">
            <img src={LOGO_URL} alt="রং ও মাটি" className="h-9 w-auto object-contain" />
            <span className="font-display text-lg text-sand">{isArtist ? "আমার রং ও মঞ্চ" : "আমার রঙের হাট"}</span>
          </button>
          <button
            data-testid="creator-logout"
            onClick={logout}
            className="rounded-full border border-red-400/30 px-4 py-1.5 text-xs text-red-300 transition-colors hover:bg-red-400 hover:text-dusk"
          >
            বেরিয়ে যান
          </button>
        </div>

        {tab !== "home" && (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button data-testid="creator-nav-home" onClick={homeNav} className="rounded-full border border-sand/25 px-4 py-1.5 text-xs text-sand/70 transition-colors hover:border-gold hover:text-gold">
              ← নিজের ঘর
            </button>
            {crumbs.map(([k, l]) => (
              <button
                key={k}
                data-testid={`creator-nav-${k}`}
                onClick={() => homeNav(k)}
                className={`rounded-full px-4 py-1.5 text-xs transition-colors ${tab === k ? "bg-gold text-dusk" : "border border-sand/25 text-sand/70 hover:border-gold hover:text-gold"}`}
              >
                {l}
              </button>
            ))}
          </div>
        )}

        <main className="mt-6 rounded-2xl border border-gold/15 bg-plaster/60 p-6 backdrop-blur-sm sm:p-8" data-testid="creator-main">
          {section()}
        </main>

        <button
          data-testid="creator-back-village"
          onClick={onExit}
          className="mt-5 w-full rounded-xl border border-gold/25 px-4 py-3 text-sm text-gold transition-colors hover:bg-gold hover:text-dusk"
        >
          ← গ্রামে ফিরুন
        </button>
      </div>
    </div>
  );
}

/* ---------- অর্ডার-রসিদ (শিল্পকার) ---------- */
function OrdersTab({ data }) {
  const orders = data.orders || [];
  return (
    <div className="max-w-3xl" data-testid="creator-section-orders">
      {orders.length === 0 && <p className="text-sm text-sand/50">এখনো কোনো জিজ্ঞেস-রসিদ আসেনি।</p>}
      <div className="space-y-3">
        {orders.map((o) => (
          <div key={o.code} className="rounded-2xl border border-gold/15 bg-dusk/50 p-5" data-testid={`my-order-${o.code}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-display text-lg text-gold">{o.code}</p>
                <p className="mt-1 text-sm text-sand">{o.name} • {o.phone} {o.place ? `• ${o.place}` : ""}</p>
                <p className="mt-1 text-xs text-sand/60">{(o.items || []).map((i) => `${i.title} — ${i.price}`).join(" | ")}</p>
                {o.note && <p className="mt-1 text-xs text-sand/50">খুঁটিনাটি: {o.note}</p>}
              </div>
              <span className="rounded-full border border-sand/30 px-3 py-1 text-[11px] text-sand/60">{o.status || "নতুন"}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- জিজ্ঞেস-বার্তা (সরল ইনবক্স) ---------- */
function MessagesTab({ data }) {
  const enquiries = data.enquiries || [];
  return (
    <div className="max-w-3xl" data-testid="creator-section-messages">
      {enquiries.length === 0 && <p className="text-sm text-sand/50">আপনার পণ্যের কোনো জিজ্ঞেস এখনো আসেনি।</p>}
      <div className="space-y-3">
        {enquiries.map((q) => (
          <div key={q.enquiry_id} className="rounded-2xl border border-gold/15 bg-dusk/50 p-5" data-testid={`my-enquiry-${q.enquiry_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm text-sand">{q.name} • {q.phone} {q.place ? `• ${q.place}` : ""}</p>
                <p className="mt-1.5 text-sm text-sand/75">{q.message}</p>
                <p className="mt-1.5 text-xs text-sand/45">{(q.items || []).map((i) => `${i.title} ×${i.qty || 1}`).join(", ")}</p>
              </div>
              <span className="rounded-full border border-sand/30 px-3 py-1 text-[11px] text-sand/55">নতুন জিজ্ঞেস</span>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-sand/45">জবাব দেবেন সরাসরি ফোনে — প্রোটোটাইপে এখান থেকে লেখা যায় না।</p>
    </div>
  );
}
